import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { formatDate } from '../../utils/ticketTransformers';

/**
 * Activity timeline tab for ticket history
 */
const ActivityTab = ({ ticket }) => {
  const { t } = useThemeSettings();
  const theme = useTheme();

  const TimelineItem = ({ color, title, date, user, isLast = false }) => (
    <Box
      sx={{
        display: 'flex',
        mb: isLast ? 0 : 2,
        pb: isLast ? 0 : 2,
        borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`
      }}
    >
      <Box
        sx={{
          mr: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: `${color}.main`,
            mb: 1
          }}
        />
        {!isLast && <Box sx={{ width: 2, height: 40, bgcolor: 'grey.300' }} />}
      </Box>
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {date} {user && `by ${user}`}
        </Typography>
      </Box>
    </Box>
  );

  const getUserName = (user) => {
    if (!user) return 'Unknown';
    return user.name ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      'Unknown';
  };

  const events = [];

  // Created event
  events.push({
    color: 'success',
    title: t ? t("Tickets.ticket_created") : "Ticket created",
    date: formatDate(ticket.created_at || ticket.createdAt),
    user: getUserName(ticket.createdByUser)
  });

  // In Progress event
  if (ticket.status !== 'Open') {
    events.push({
      color: 'warning',
      title: t ? t("Tickets.status_changed_progress") : "Status changed to In Progress",
      date: formatDate(ticket.updated_at || ticket.updatedAt),
      user: t ? t("Tickets.support_team") : "Support Team"
    });
  }

  // Closed event
  if (ticket.status === 'Closed') {
    events.push({
      color: 'success',
      title: t ? t("Tickets.ticket_closed") : "Ticket closed",
      date: formatDate(ticket.updated_at || ticket.updatedAt),
      user: getUserName(ticket.supportUser)
    });
  }

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" sx={{ mb: 3 }}>
        {t ? t("Tickets.track_history_actions_taken_th") : "Track the history of actions taken on this ticket."}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {events.map((event, index) => (
          <TimelineItem
            key={index}
            color={event.color}
            title={event.title}
            date={event.date}
            user={event.user}
            isLast={index === events.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ActivityTab;
